import { ApiError } from '../core/errors'

export { ApiError }

export interface RequestOptions {
  signal?: AbortSignal
  timeoutMs?: number | null
  /** Upload progress resets this deadline; active transfers have no total time limit. */
  inactivityMs?: number
  headers?: HeadersInit
}

async function request<T>(path: string, init: RequestInit = {}, options: RequestOptions = {}): Promise<T> {
  const controller = new AbortController()
  const timeout = options.timeoutMs === null ? null : window.setTimeout(() => controller.abort(), options.timeoutMs ?? 12_000)
  const signal = options.signal
  const abort = () => controller.abort()
  signal?.addEventListener('abort', abort, { once: true })
  try {
    let response: Response
    try {
      const requestInit: RequestInit = {
        ...init,
        headers: { Accept: 'application/json', ...(init.headers ?? {}), ...(options.headers ?? {}) },
      }
      // Vitest on Node 26 can pair Node's fetch with jsdom's AbortSignal. A
      // cross-realm signal is rejected before MSW sees the request; detect that
      // mismatch and let the request proceed without cancellation in that test
      // worker. Real browsers use one realm, so timeout/query cancellation stays
      // active in production.
      try {
        new Request(path, { signal: controller.signal })
        requestInit.signal = controller.signal
      } catch {
        // Keep the request itself usable; the timeout is still cleared below.
      }
      response = await fetch(path, requestInit)
    } catch (error) {
      if (controller.signal.aborted && !signal?.aborted) {
        throw new ApiError(408, 'The request timed out.', 'Try again in a moment.')
      }
      throw new ApiError(0, 'The console is unreachable.', error instanceof Error ? error.message : '')
    }

    const text = await response.text()
    let body: unknown = null
    if (text) {
      try {
        body = JSON.parse(text)
      } catch {
        body = null
      }
    }
    if (!response.ok) {
      const record = (body ?? {}) as { error?: string; detail?: string; message?: string }
      throw new ApiError(
        response.status,
        record.error ?? record.message ?? `Request failed with status ${response.status}`,
        record.detail ?? '',
      )
    }
    return body as T
  } finally {
    if (timeout !== null) window.clearTimeout(timeout)
    signal?.removeEventListener('abort', abort)
  }
}

export function get<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>(path, {}, options)
}

export function post<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    options,
  )
}

export function put<T>(path: string, body: BodyInit, options?: RequestOptions): Promise<T> {
  if (body instanceof Blob && options?.inactivityMs !== undefined) return upload<T>(path, body, options)
  return request<T>(
    path,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body,
    },
    options,
  )
}

function upload<T>(path: string, body: Blob, options: RequestOptions): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    let timer: ReturnType<typeof setTimeout>
    let inactive = false
    const abort = () => xhr.abort()
    const cleanup = () => { clearTimeout(timer); options.signal?.removeEventListener('abort', abort) }
    const activity = () => { clearTimeout(timer); timer = setTimeout(() => { inactive = true; xhr.abort() }, options.inactivityMs) }
    xhr.open('PUT', path)
    new Headers({ Accept: 'application/json', 'Content-Type': 'application/octet-stream', ...Object.fromEntries(new Headers(options.headers)) }).forEach((value, key) => xhr.setRequestHeader(key, value))
    xhr.upload.onprogress = activity
    xhr.onprogress = activity
    xhr.onload = () => {
      cleanup()
      let response: unknown = null
      try { response = JSON.parse(xhr.responseText) } catch { /* An invalid response cannot supply an upload offset. */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(response as T)
      else {
        const record = (response ?? {}) as { error?: string; detail?: string; message?: string }
        reject(new ApiError(xhr.status, record.error ?? record.message ?? `Request failed with status ${xhr.status}`, record.detail ?? ''))
      }
    }
    xhr.onerror = () => { cleanup(); reject(new ApiError(0, 'The console is unreachable.')) }
    xhr.onabort = () => { cleanup(); reject(new ApiError(inactive ? 408 : 0, inactive ? 'The upload stopped making progress. Retry to resume.' : 'The upload was cancelled.')) }
    options.signal?.addEventListener('abort', abort, { once: true })
    if (options.signal?.aborted) { cleanup(); reject(new ApiError(0, 'The upload was cancelled.')); return }
    activity()
    xhr.send(body)
  })
}

export function patch<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    options,
  )
}
